import type { AccountType } from '../types/account.js';
import type {
  PortalAccountSummary,
  PortalPermission,
  PortalUserStatus,
} from '../types/portalUser.js';

// CLIENT PORTAL — SCOPE RESOLUTION. The one place a portal user's grants become
// a set of account ids, and the one place that set can be narrowed.
//
// ⚠️⚠️ READ BEFORE CHANGING ANYTHING HERE. This function decides which client
// sees which numbers. The realistic failure is not "a bug" in the abstract: it
// is a Mazda dealer in Alexandria seeing the Mazda dealer in Waterloo's cost per
// lead, or one franchisee seeing another's spend. Both are commercial damage we
// cannot take back once it has been rendered on a screen.
//
// THREE PROPERTIES, each enforced structurally rather than by convention:
//
// 1. A `PortalScope` cannot be constructed by hand. It carries a `unique symbol`
//    brand, so the ONLY way to obtain one is `resolvePortalScope`. A portal
//    query function declares `scope: PortalScope` as a REQUIRED argument — so an
//    unscoped portal query is a type error at the call site, not a bug that
//    ships and leaks.
//
// 4. ⚠️⚠️ THE GRANTS COME FROM THE CONTACT, NOT FROM A STORED COPY (2026-09-02).
//    `PortalUser` no longer carries an `accountIds` snapshot: the person's
//    account links ARE the grant, resolved fresh on every request. The bug that
//    forced this: a contact linked to two locations was invited from one, saw
//    only that one, and re-sharing from the second returned "that email already
//    has portal access" and did nothing — an error that was true and useless.
//    A snapshot cannot help drifting from the thing it was copied from.
//
//    ⚠️ THIS DOES NOT GRANT ACCESS TO CONTACTS. Invitation is still explicit and
//    staff-initiated: a `portalUsers` record must exist, and `findPortalIdentity`
//    is what admits anybody. What changed is where an ADMITTED person's scope is
//    read from. Contacts are created by CSV imports, bulk onboarding and
//    lead-bot captures — a captured gym member is a contact — so auto-granting
//    would make a spreadsheet upload an authorisation change.
//
// 2. Narrowing only. `selectWithinScope` returns a scope or NULL; it can never
//    return a scope containing an id the parent scope did not. A client-supplied
//    accountId therefore *selects within* the resolved scope and can never widen
//    it — rule 3 of the build plan, made unrepresentable rather than remembered.
//
// 3. Empty is not absent. `scopeQuery` returns a DISCRIMINATED UNION whose empty
//    case cannot be mistaken for "no filter". This is the `empty ≠ absent` rule
//    that has already bitten the reporting directory filter and the AM home
//    (where an empty array reaching BigQuery threw, and coercing it to
//    `undefined` would have shown the whole agency's spend). Here the same slip
//    would show a client the whole roster, so the caller is made to branch.

/** Nominal brand. Declared, never exported — a scope cannot be forged. */
declare const PORTAL_SCOPE_BRAND: unique symbol;

export type ScopeAccount = {
  id: string;
  name: string;
  parentAccountId: string | null;
  accountType: AccountType;
};

export type PortalScope = {
  readonly [PORTAL_SCOPE_BRAND]: true;
  /**
   * The OPERATIONAL data scope — locations + dealerships only. Every report,
   * every asset list, every future data query filters to exactly this set.
   */
  readonly accountIds: readonly string[];
  /** The raw grants that resolved (ids that don't exist are dropped). */
  readonly grantedAccountIds: readonly string[];
  /**
   * What a client-supplied accountId is allowed to name: every grant and
   * everything beneath it, of any type. Naming an intermediate group is a
   * legitimate drill-down (it resolves to a SUBSET of the operational scope);
   * naming anything else is refused. Never widens.
   */
  readonly selectableAccountIds: readonly string[];
  /**
   * The accounts worth SHOWING: the grants plus their operational descendants.
   * Deliberately narrower than `selectableAccountIds` — intermediate groups are
   * selectable but are structural plumbing, not something a client needs listed.
   * Grant-first, then alphabetical.
   */
  readonly accounts: readonly PortalAccountSummary[];
  readonly permissions: readonly PortalPermission[];
};

/**
 * The account types that carry data. Mirrors OPERATIONAL_ACCOUNT_TYPES in
 * src/lib/accountAnalytics.ts — kept as its own constant rather than imported so
 * this module stays a pure leaf with no dependency on the analytics stack, and
 * pinned to that list by a test so the two cannot silently diverge.
 */
export const PORTAL_OPERATIONAL_TYPES: readonly AccountType[] = ['location', 'dealership'];

function isOperational(type: AccountType): boolean {
  return PORTAL_OPERATIONAL_TYPES.includes(type);
}

/**
 * What `resolvePortalScope` needs. Deliberately NOT `Pick<PortalUser, ...>`.
 *
 * ⚠️⚠️ THE FIELD IS `grantedAccountIds`, AND THE NAME IS THE GUARD. TypeScript is
 * structural, so a type spelled `{ accountIds; permissions; status }` would still
 * accept a whole `PortalUser` — which is exactly the stored snapshot this change
 * exists to stop being an authority. Under this name a `PortalUser` does not
 * satisfy the shape, so re-introducing the old call is a compile error rather
 * than a silent reversion.
 */
export type PortalGrants = {
  /** ⚠️ The CONTACT'S links (see `contactGrantAccountIds`), never a stored copy. */
  readonly grantedAccountIds: readonly string[];
  readonly permissions: readonly PortalPermission[];
  readonly status: PortalUserStatus;
};

/**
 * The account ids a contact's links grant.
 *
 * ⚠️⚠️ IT READS `accountLinks`, THE SOURCE — NEVER `accountIds`, THE CACHE.
 * `src/types/contact.ts` says outright that `accountIds` is denormalised "purely
 * for query efficiency" because Firestore cannot query a nested array of
 * objects. Deriving AUTHORISATION from a denormalised copy means scope drifts
 * the day the denormalisation does, which is the same class of defect as the
 * snapshot this replaces — just one layer further in. Scope resolution already
 * holds the contact document, so reading the links costs nothing.
 *
 * ⚠️ A missing contact yields NO grants, so a portal user whose contact was
 * deleted fails CLOSED. Duplicates are collapsed; order is not significant, and
 * the resolver sorts what it emits.
 */
export function contactGrantAccountIds(
  contact: { accountLinks?: readonly { accountId: string }[] } | null | undefined,
): string[] {
  if (!contact?.accountLinks) return [];
  const out = new Set<string>();
  for (const link of contact.accountLinks) {
    const id = typeof link?.accountId === 'string' ? link.accountId.trim() : '';
    if (id) out.add(id);
  }
  return [...out];
}

/**
 * Resolve a portal user's grants into their effective scope.
 *
 * ⚠️ A `disabled` user resolves to an EMPTY scope. Disabling is not a UI filter;
 * it removes the data at the one place data access is decided, so no endpoint
 * added later can forget to check the status.
 *
 * ⚠️ Grants pointing at accounts that no longer exist are DROPPED, not treated
 * as wildcards. A deleted account must shrink a scope, never widen one.
 *
 * Status of the account itself (active / paused / cancelled) is deliberately NOT
 * filtered: a paused client's own historical reports are still legitimately
 * theirs, and hiding them would look like data loss rather than access control.
 */
export function resolvePortalScope(
  grants: PortalGrants,
  accounts: readonly ScopeAccount[],
): PortalScope {
  // ⚠️ FIRST, AND UNCONDITIONALLY. Revocation must not be undone by the move to
  // contact-derived grants: a disabled user resolves to nothing whatever their
  // contact's links say, and the check sits ahead of every read of them.
  if (grants.status === 'disabled') return emptyScope();

  const byId = new Map<string, ScopeAccount>();
  for (const a of accounts) byId.set(a.id, a);
  const childrenByParent = buildChildIndex(accounts);

  const granted: string[] = [];
  const operational = new Set<string>();
  const selectable = new Set<string>();

  for (const grantId of grants.grantedAccountIds) {
    const grant = byId.get(grantId);
    if (!grant) continue; // a grant we cannot resolve grants nothing
    granted.push(grantId);
    for (const id of descendantsOf(grantId, childrenByParent)) {
      const acc = byId.get(id);
      if (!acc) continue;
      // Everything beneath a grant may be NAMED (drill-down); only operational
      // accounts enter the DATA scope.
      selectable.add(id);
      if (isOperational(acc.accountType)) operational.add(id);
    }
  }

  const grantSet = new Set(granted);
  return buildScope({
    accountIds: [...operational].sort(),
    grantedAccountIds: [...grantSet].sort(),
    selectableAccountIds: [...selectable].sort(),
    accounts: summarise(renderable(grantSet, operational), grantSet, byId),
    permissions: [...grants.permissions],
  });
}

/**
 * Narrow a scope to ONE selected account.
 *
 * Returns `null` when the requested id is not selectable — the caller refuses
 * the request rather than serving a default. ⚠️ Never fall back to the full
 * scope on a bad id: "the parameter was wrong so I showed you everything you're
 * allowed" is a plausible-looking behaviour that turns a typo into a data dump
 * the client did not ask for and cannot tell apart from a correct response.
 *
 * A group selection narrows to that group's operational descendants; a location
 * selection narrows to itself. Either way the result is a subset of the parent
 * scope by construction — the intersections below are what make widening
 * impossible even if the tree changed between the two calls.
 */
export function selectWithinScope(
  scope: PortalScope,
  requestedAccountId: string,
  accounts: readonly ScopeAccount[],
): PortalScope | null {
  if (!scope.selectableAccountIds.includes(requestedAccountId)) return null;

  const byId = new Map<string, ScopeAccount>();
  for (const a of accounts) byId.set(a.id, a);
  const childrenByParent = buildChildIndex(accounts);

  const parentOperational = new Set(scope.accountIds);
  const parentSelectable = new Set(scope.selectableAccountIds);

  const operational = new Set<string>();
  const selectable = new Set<string>([requestedAccountId]);
  for (const id of descendantsOf(requestedAccountId, childrenByParent)) {
    const acc = byId.get(id);
    if (!acc) continue;
    // ⚠️ Both intersections are load-bearing: even if the tree gained a child
    // between the parent resolve and this call, an id the parent scope never
    // granted cannot enter here.
    if (parentSelectable.has(id)) selectable.add(id);
    if (!isOperational(acc.accountType)) continue;
    if (!parentOperational.has(id)) continue;
    operational.add(id);
  }

  const grantSet = new Set([requestedAccountId]);
  return buildScope({
    accountIds: [...operational].sort(),
    grantedAccountIds: [requestedAccountId],
    selectableAccountIds: [...selectable].sort(),
    accounts: summarise(renderable(grantSet, operational), grantSet, byId),
    permissions: [...scope.permissions],
  });
}

/**
 * ⚠️ THE `empty ≠ absent` CHOKEPOINT. A discriminated union, so a caller cannot
 * hand an empty id list to a query layer that reads "no ids" as "no filter".
 * `{ kind: 'empty' }` has no `accountIds` to spread, forget, or coerce.
 */
export type ScopeQuery = { kind: 'empty' } | { kind: 'ids'; accountIds: string[] };

/**
 * Narrow to SEVERAL accounts the client asked for (§9, 2026-09-03).
 *
 * ⚠️⚠️ THE SECURITY RULE IS THE WHOLE OF THIS FUNCTION, and every part of
 * `selectWithinScope`'s contract survives the move from one id to many:
 *
 *   · the granted scope is resolved server-side from the contact's account
 *     links, exactly as before — this only ever NARROWS what that produced;
 *   · the requested ids are INTERSECTED with it;
 *   · ⚠️ ANY id outside the scope makes the whole request a 404 — NOT a silent
 *     drop, and NOT a fallback to everything. A tampered parameter must get the
 *     nothing it asked for, not "here is everything you're allowed". Dropping
 *     the bad id and serving the rest is the tempting shape and it is wrong:
 *     it turns a probe into a partial success and tells the prober which ids
 *     are real.
 *   · ⚠️⚠️ AN EMPTY SELECTION IS "FILTERED TO NOTHING", NOT "NO FILTER". It
 *     returns an EMPTY scope, whose `scopeQuery` is `{kind:'empty'}` and reads
 *     nothing at all — the same `empty ≠ absent` rule `campaignIds` draws, and
 *     the one this codebase has been bitten by twice.
 *
 * ⚠️ RETURNS `null` FOR A REFUSAL and an empty SCOPE for an empty selection —
 * two different outcomes that must not collapse into one. The caller 404s the
 * first and serves an empty report for the second.
 *
 * ⚠️ ONE id behaves EXACTLY as `selectWithinScope` does, and that is asserted
 * rather than assumed: a second narrowing path that disagreed with the first
 * about what a single account means is precisely the drift `resolvePortalScope`
 * exists to prevent.
 */
export function selectManyWithinScope(
  scope: PortalScope,
  requestedAccountIds: readonly string[],
  accounts: readonly ScopeAccount[],
): PortalScope | null {
  // ⚠️ DE-DUPED but NOT re-ordered before the membership check — a repeated id
  // is a harmless client quirk, an unknown one is a refusal, and collapsing
  // duplicates must not let a bad id hide behind a good one.
  const requested = [...new Set(requestedAccountIds)];
  for (const id of requested) {
    if (!scope.selectableAccountIds.includes(id)) return null;
  }

  if (requested.length === 0) {
    // ⚠️ An EMPTY scope, built through the same builder rather than hand-rolled,
    // so it carries the same shape every other scope does — and `scopeQuery`
    // reads it as `empty`, which means "read nothing".
    return buildScope({
      accountIds: [],
      grantedAccountIds: [],
      selectableAccountIds: [],
      accounts: [],
      permissions: [...scope.permissions],
    });
  }

  // ⚠️ ONE id delegates, rather than reimplementing the single case. Identical
  // by construction, not by two implementations agreeing.
  if (requested.length === 1) {
    return selectWithinScope(scope, requested[0] as string, accounts);
  }

  const byId = new Map<string, ScopeAccount>();
  for (const a of accounts) byId.set(a.id, a);
  const childrenByParent = buildChildIndex(accounts);

  const parentOperational = new Set(scope.accountIds);
  const parentSelectable = new Set(scope.selectableAccountIds);

  const operational = new Set<string>();
  const selectable = new Set<string>(requested);
  for (const requestedId of requested) {
    for (const id of descendantsOf(requestedId, childrenByParent)) {
      const acc = byId.get(id);
      if (!acc) continue;
      // ⚠️ Both intersections are load-bearing, for the same reason they are in
      // the single-id twin: even if the tree gained a child between the parent
      // resolve and this call, an id the parent scope never granted cannot
      // enter here.
      if (parentSelectable.has(id)) selectable.add(id);
      if (!isOperational(acc.accountType)) continue;
      if (!parentOperational.has(id)) continue;
      operational.add(id);
    }
  }

  const grantSet = new Set(requested);
  return buildScope({
    accountIds: [...operational].sort(),
    grantedAccountIds: [...requested].sort(),
    selectableAccountIds: [...selectable].sort(),
    accounts: summarise(renderable(grantSet, operational), grantSet, byId),
    permissions: [...scope.permissions],
  });
}

export function scopeQuery(scope: PortalScope): ScopeQuery {
  if (scope.accountIds.length === 0) return { kind: 'empty' };
  return { kind: 'ids', accountIds: [...scope.accountIds] };
}

/** True when this scope grants access to no data at all. */
export function isScopeEmpty(scope: PortalScope): boolean {
  return scope.selectableAccountIds.length === 0;
}

export function hasPortalPermission(scope: PortalScope, permission: PortalPermission): boolean {
  return scope.permissions.includes(permission);
}

// ---------- internals ----------

function emptyScope(): PortalScope {
  return buildScope({
    accountIds: [],
    grantedAccountIds: [],
    selectableAccountIds: [],
    accounts: [],
    permissions: [],
  });
}

function buildScope(parts: {
  accountIds: string[];
  grantedAccountIds: string[];
  selectableAccountIds: string[];
  accounts: PortalAccountSummary[];
  permissions: PortalPermission[];
}): PortalScope {
  // The brand is TYPE-ONLY (a `declare`d unique symbol has no runtime value),
  // so the object carries no extra key — the cast is what confers it.
  return parts as unknown as PortalScope;
}

function buildChildIndex(accounts: readonly ScopeAccount[]): Map<string, string[]> {
  const childrenByParent = new Map<string, string[]>();
  for (const a of accounts) {
    if (a.parentAccountId == null) continue;
    const arr = childrenByParent.get(a.parentAccountId);
    if (arr) arr.push(a.id);
    else childrenByParent.set(a.parentAccountId, [a.id]);
  }
  return childrenByParent;
}

/** The node plus every descendant, walking parentAccountId DOWNWARD. Cycle-guarded. */
function descendantsOf(rootId: string, childrenByParent: Map<string, string[]>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop() as string;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const child of childrenByParent.get(id) ?? []) stack.push(child);
  }
  return out;
}

/** The ids worth rendering: the grants plus their operational descendants. */
function renderable(grants: Set<string>, operational: Set<string>): Set<string> {
  return new Set<string>([...grants, ...operational]);
}

function summarise(
  ids: Set<string>,
  grants: Set<string>,
  byId: Map<string, ScopeAccount>,
): PortalAccountSummary[] {
  const rows: PortalAccountSummary[] = [];
  for (const id of ids) {
    const acc = byId.get(id);
    if (!acc) continue;
    rows.push({
      id: acc.id,
      name: acc.name,
      accountType: acc.accountType,
      isGrant: grants.has(id),
      parentAccountId: acc.parentAccountId,
    });
  }
  // Grants first (a group heads its own locations), then alphabetical.
  return rows.sort((a, b) => {
    if (a.isGrant !== b.isGrant) return a.isGrant ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}
